import {
  getGroupMemberInstallQty,
  getGroupMemberProductQty,
  resolveGroupDisplayBaseEach,
  resolveGroupProductMarketingAdjustment,
  resolveGroupInstallMarketingAdjustment,
} from 'src/pages/dashboard/quotation/quotationGroupingHelpers'

export const serializeDesignOrderGroupedRowsForInvoice = (rows = []) =>
  (rows || []).map((row) => {
    if (row?.__type !== 'group') return row
    return {
      __type: 'group',
      groupId: row.groupId,
      representative: {
        group_name: row.representative?.group_name,
        group_description: row.representative?.group_description,
      },
      members: (row.members || []).map((member) => ({
        install_notes: member?.install_notes,
      })),
      groupMeta: row.groupMeta,
      groupProductDisplayQty: row.groupProductDisplayQty,
      groupInstallDisplayQty: row.groupInstallDisplayQty,
      groupHasInstall: row.groupHasInstall,
      groupInstallSubtotal: row.groupInstallSubtotal,
      totalValue: row.totalValue,
      groupProductOnlyTotal: row.groupProductOnlyTotal,
      adjustedGroupRetailEach: row.adjustedGroupRetailEach,
      adjustedGroupInstallRetailEach: row.adjustedGroupInstallRetailEach,
      adjustedGroupSubtotal: row.adjustedGroupSubtotal,
      adjustedGroupInstallSubtotal: row.adjustedGroupInstallSubtotal,
      adjustedGroupTotal: row.adjustedGroupTotal,
    }
  })

export const getDesignOrderProductGroupId = (product) =>
  product?.group_id ??
  product?.customer_order_product_group_id ??
  product?.groupId ??
  null

/** Resolve catalog/line name for invoice, print, and email rows. */
export const getInvoiceProductDisplayName = (item = {}) => {
  const resolved = [item?.product_name, item?.name, item?.catalog_product_name]
    .map((value) => (value != null ? String(value).trim() : ''))
    .find(Boolean)
  return resolved || ''
}

export const withInvoiceProductDisplayName = (product = {}) => {
  const product_name = getInvoiceProductDisplayName(product)
  return product_name ? { ...product, product_name } : product
}

const resolveDesignOrderGroupMetaNumber = (value) => {
  if (value == null || value === '') return null
  const num = Number(value)
  return Number.isFinite(num) ? num : null
}

const getDesignOrderGroupMemberProductRetailEach = (product, markupType) =>
  Number(
    markupType === 1
      ? product?.product_price ?? product?.price ?? 0
      : product?.product_retail_price ??
          product?.actual_retail_price ??
          product?.retail_price ??
          0,
  )

const getDesignOrderGroupMemberInstallSubtotal = (product) => {
  const installRetailEach = Number(product?.install_cost || 0)
  if (!(installRetailEach > 0)) return 0
  const installQty = Number(product?.install_quantity ?? product?.install_qty ?? 1) || 1
  return installRetailEach * installQty
}

export const buildGroupedDesignOrderDisplayRows = ({
  designOrderProducts = [],
  customerOrderProductGroups = [],
  markupType = 2,
  groupProductMarketingValues = {},
  groupInstallMarketingValues = {},
} = {}) => {
  const source = sortDesignOrderProductsByIndex(designOrderProducts)
  const groupMetaById = new Map(
    (customerOrderProductGroups || []).map((g) => [String(g?.id ?? g?.group_id), g]),
  )
  const out = []
  const seenGroups = new Set()
  const resolvedMarkupType = Number(markupType) || 2

  source.forEach((product) => {
    const rawGroupId = getDesignOrderProductGroupId(product)
    if (!rawGroupId) {
      out.push(withInvoiceProductDisplayName(product))
      return
    }
    const groupId = String(rawGroupId)
    if (seenGroups.has(groupId)) return
    seenGroups.add(groupId)

    const members = source.filter(
      (item) => String(getDesignOrderProductGroupId(item) || '') === groupId,
    )
    const representative = members?.[0] || product
    const groupMeta = groupMetaById.get(groupId) || {}

    const metaProdQty =
      groupMeta.product_marketing_quantity ?? groupMeta.productMarketingQuantity
    // Design Order groups use set-multiplier qty from group meta (default 1 set).
    const groupProductDisplayQty = Math.max(1, Number(metaProdQty ?? 1) || 1)

    const metaInstQty =
      groupMeta.install_marketing_quantity ?? groupMeta.installMarketingQuantity
    const groupInstallDisplayQty = Math.max(
      1,
      Number(metaInstQty ?? groupProductDisplayQty ?? 1) || 1,
    )

    const memberProductSetSubtotal = (members || []).reduce(
      (sum, p) =>
        sum +
        getDesignOrderGroupMemberProductRetailEach(p, resolvedMarkupType) *
          getGroupMemberProductQty(p),
      0,
    )
    const memberInstallSetSubtotal = (members || []).reduce((sum, p) => {
      const installEach = Number(p?.install_cost || 0)
      if (!(installEach > 0)) return sum
      return sum + installEach * getGroupMemberInstallQty(p)
    }, 0)
    const memberInstallSubtotalSum = members.reduce(
      (sum, p) => sum + getDesignOrderGroupMemberInstallSubtotal(p),
      0,
    )

    const totalProductRetailEach = resolveGroupDisplayBaseEach(
      memberProductSetSubtotal,
      groupMeta.product_total_retail_each ?? groupMeta.productTotalRetailEach,
    )
    const totalInstallRetailEach = resolveGroupDisplayBaseEach(
      memberInstallSetSubtotal,
      groupMeta.install_total_retail_each ?? groupMeta.installTotalRetailEach,
    )

    const productMarketing = groupProductMarketingValues?.[groupId] || {}
    const installMarketing = groupInstallMarketingValues?.[groupId] || {}

    const groupAdjustment = resolveGroupProductMarketingAdjustment(
      productMarketing,
      groupMeta,
    )
    const groupAdjValueType =
      productMarketing.adjValueType ??
      groupMeta.product_marketing_adj_value_type ??
      groupMeta.productMarketingAdjValueType ??
      representative?.adj_value_type ??
      true
    const groupInstallAdjustment = resolveGroupInstallMarketingAdjustment(
      installMarketing,
      groupMeta,
    )
    const groupInstallAdjValueType =
      installMarketing.adjValueType ??
      groupMeta.install_marketing_adj_value_type ??
      groupMeta.installMarketingAdjValueType ??
      representative?.install_adj_value_type ??
      true

    const productSignedAdj =
      groupAdjValueType === false || Number(groupAdjValueType) === 0
        ? -groupAdjustment
        : groupAdjustment
    const installSignedAdj =
      groupInstallAdjValueType === false || Number(groupInstallAdjValueType) === 0
        ? -groupInstallAdjustment
        : groupInstallAdjustment

    const metaProductAdjustedEach = resolveDesignOrderGroupMetaNumber(
      groupMeta.product_adjusted_retail_each ?? groupMeta.productAdjustedRetailEach,
    )
    const metaInstallAdjustedEach = resolveDesignOrderGroupMetaNumber(
      groupMeta.install_adjusted_retail_each ?? groupMeta.installAdjustedRetailEach,
    )

    const adjustedGroupRetailEach =
      memberProductSetSubtotal > 0
        ? totalProductRetailEach + productSignedAdj
        : metaProductAdjustedEach != null
          ? metaProductAdjustedEach
          : totalProductRetailEach + productSignedAdj
    const adjustedGroupInstallRetailEach =
      memberInstallSetSubtotal > 0
        ? totalInstallRetailEach + installSignedAdj
        : metaInstallAdjustedEach != null
          ? metaInstallAdjustedEach
          : totalInstallRetailEach + installSignedAdj
    const adjustedGroupSubtotal = adjustedGroupRetailEach * groupProductDisplayQty
    const adjustedGroupInstallSubtotal = adjustedGroupInstallRetailEach * groupInstallDisplayQty
    const adjustedGroupTotal = Math.max(0, adjustedGroupSubtotal + adjustedGroupInstallSubtotal)
    const groupHasInstall =
      memberInstallSubtotalSum > 0 ||
      memberInstallSetSubtotal > 0 ||
      totalInstallRetailEach > 0 ||
      adjustedGroupInstallSubtotal > 0

    out.push({
      __type: 'group',
      groupId,
      representative,
      members,
      groupMeta,
      groupProductDisplayQty,
      groupInstallDisplayQty,
      groupHasInstall,
      groupInstallSubtotal: adjustedGroupInstallSubtotal,
      totalValue: adjustedGroupTotal,
      groupProductOnlyTotal: adjustedGroupSubtotal,
      adjustedGroupRetailEach,
      adjustedGroupInstallRetailEach,
      adjustedGroupSubtotal,
      adjustedGroupInstallSubtotal,
      adjustedGroupTotal,
    })
  })

  return out
}

export const sortDesignOrderProductsByIndex = (products) =>
  [...(products || [])].sort(
    (a, b) =>
      (Number(a?.product_index) || 0) - (Number(b?.product_index) || 0) ||
      (Number(a?.id) || 0) - (Number(b?.id) || 0),
  )

export const getDesignOrderProductUnitPrice = (item) =>
  Number(item?.actual_retail_price ?? item?.retail_price ?? item?.price ?? 0)

export const getDesignOrderProductQty = (item) =>
  Number(item?.quantity ?? item?.locationQuantity ?? 1)

export const getDesignOrderProductSubtotal = (item) => {
  if (item?.product_sub_total != null) return Number(item.product_sub_total)
  const unit = getDesignOrderProductUnitPrice(item)
  const qty = getDesignOrderProductQty(item)
  return unit * qty
}

export const getDesignOrderProductGrandTotal = (item) => {
  const installCost = parseFloat(item?.install_cost) || 0
  const subtotal = getDesignOrderProductSubtotal(item)
  if (installCost > 0) {
    const productQty = getDesignOrderProductQty(item)
    const installQty = Number(item?.install_quantity ?? item?.install_qty ?? productQty) || productQty
    return subtotal + installCost * installQty
  }
  if (item?.product_total != null) return Number(item.product_total)
  return subtotal
}

export const getDesignOrderProductInstallTotal = (item) => {
  const installCost = parseFloat(item?.install_cost) || 0
  const productQty = getDesignOrderProductQty(item)
  const installQty = Number(item?.install_quantity ?? item?.install_qty ?? productQty) || productQty
  return installCost > 0 ? installCost * installQty : 0
}

export const designOrderProductShowsPricing = (item) =>
  item?.is_product_row !== 1 ||
  getDesignOrderProductUnitPrice(item) > 0 ||
  item?.product_sub_total != null ||
  item?.product_total != null

export const getDesignOrderServiceTotal = (service) => {
  if (service?.total != null) return Number(service.total) || 0
  if (service?.total_cost != null) return Number(service.total_cost) || 0
  return (Number(service?.unit_cost) || 0) * (Number(service?.quantity) || 1)
}

export const getDesignOrderServiceUnitCost = (service) => {
  if (service?.unit_cost != null) return Number(service.unit_cost) || 0
  const qty = Number(service?.quantity) || 1
  return qty ? getDesignOrderServiceTotal(service) / qty : 0
}

export const getDesignOrderServiceQuantity = (service) =>
  Number(service?.quantity ?? service?.qty ?? 1) || 0

export const mapDesignOrderProductsToCustProduct = (products) =>
  sortDesignOrderProductsByIndex(products).map((item, index) => {
    const unitPrice = getDesignOrderProductUnitPrice(item)
    const qty = getDesignOrderProductQty(item)
    const lineSubtotal = getDesignOrderProductSubtotal(item)
    const groupProductQty = getGroupMemberProductQty(item)
    const groupInstallQty = getGroupMemberInstallQty(item)
    return {
      product_name: getInvoiceProductDisplayName(item),
      product_notes: item?.product_notes || '',
      install_notes: item?.install_notes || '',
      product_retail_price: unitPrice,
      actual_retail_price: unitPrice,
      quantity: 1,
      locationQuantity: qty,
      group_product_quantity: groupProductQty,
      group_install_quantity: groupInstallQty,
      install_cost: parseFloat(item?.install_cost) || 0,
      install_qty: groupInstallQty,
      install_quantity: groupInstallQty,
      product_total: getDesignOrderProductGrandTotal(item),
      product_sub_total: lineSubtotal,
      product_index: item?.product_index ?? index + 1,
      group_id: getDesignOrderProductGroupId(item),
      customer_order_product_group_id: getDesignOrderProductGroupId(item),
      material: 0,
      rfp: item?.rfp ?? 0,
      markAdjType: 1,
      adjustment: 0,
      is_product_row: item?.is_product_row,
      vpt_number: item?.vpt_number || item?.sku || '',
    }
  })

export const mapDesignOrderServicesForInvoice = (services) =>
  (services || []).map((service) => ({
    ...service,
    name: service?.name || service?.service_name || '',
    service_name: service?.name || service?.service_name || '',
    total_cost: getDesignOrderServiceTotal(service),
    quantity: getDesignOrderServiceQuantity(service),
  }))

export const computeDesignOrderInvoiceTotals = ({
  designOrderProducts = [],
  designOrderServices = [],
  details = {},
  invoice = {},
  customerOrderProductGroups = [],
  markupType,
  groupProductMarketingValues = {},
  groupInstallMarketingValues = {},
}) => {
  const hasGroupedProducts = (designOrderProducts || []).some((item) =>
    getDesignOrderProductGroupId(item),
  )
  const resolvedMarkupType =
    markupType != null && markupType !== ''
      ? Number(markupType)
      : Number(details?.markup_type ?? invoice?.markup_type ?? 2) || 2

  const getNonGroupProductSubtotal = (item) =>
    designOrderProductShowsPricing(item) ? getDesignOrderProductSubtotal(item) : 0

  let productTotal = 0
  let productInstallTotal = 0

  if (hasGroupedProducts) {
    const displayRows = buildGroupedDesignOrderDisplayRows({
      designOrderProducts,
      customerOrderProductGroups,
      markupType: resolvedMarkupType,
      groupProductMarketingValues,
      groupInstallMarketingValues,
    })
    productTotal = displayRows.reduce((sum, row) => {
      if (row?.__type === 'group') {
        const groupHasInstall = Boolean(row?.groupHasInstall)
        return (
          sum +
          (groupHasInstall
            ? Number(row?.groupProductOnlyTotal ?? 0)
            : Number(row?.totalValue ?? 0))
        )
      }
      return sum + getNonGroupProductSubtotal(row)
    }, 0)
    productInstallTotal = displayRows.reduce((sum, row) => {
      if (row?.__type === 'group') {
        return sum + Number(row?.groupInstallSubtotal ?? 0)
      }
      return sum + getDesignOrderProductInstallTotal(row)
    }, 0)
  } else {
    productTotal = designOrderProducts.reduce(
      (sum, item) => sum + getNonGroupProductSubtotal(item),
      0,
    )
    productInstallTotal = designOrderProducts.reduce(
      (sum, item) => sum + getDesignOrderProductInstallTotal(item),
      0,
    )
  }
  const installServiceTotal = designOrderServices.reduce(
    (sum, service) =>
      Number(service?.labour_cost_service_id) === 10
        ? sum + getDesignOrderServiceTotal(service)
        : sum,
    0,
  )
  const additionalServicesTotal = designOrderServices.length
    ? designOrderServices.reduce(
        (sum, service) =>
          Number(service?.labour_cost_service_id) === 10
            ? sum
            : sum + getDesignOrderServiceTotal(service),
        0,
      )
    : 0
  const installTotal = productInstallTotal + installServiceTotal
  const subtotal = productTotal + installTotal + additionalServicesTotal
  const discount = Number(invoice?.extra_discount ?? details?.extra_discount ?? 0) || 0
  const tax = Number(invoice?.tax_amount ?? details?.tax_amount ?? details?.tax ?? 0) || 0
  const total = subtotal - discount + tax

  return {
    productTotal,
    productInstallTotal,
    installServiceTotal,
    additionalServicesTotal,
    installTotal,
    subtotal,
    discount,
    tax,
    total,
  }
}

export const getDesignOrderProjectCommunity = (communities, details) =>
  (communities || []).find((c) => c.id === details?.community) || null
